import { config } from '@/config';
import { ApiError } from '@/lib/api-client';

// 429 is the public redeem endpoint's abuse throttle — a throttled redemption
// is an expected outcome, not an unexpected failure worth a Sentry report.
const EXPECTED_REDEEM_STATUSES = new Set([400, 401, 403, 429]);

/** User-facing invite redemption failures that should not be reported to Sentry. */
export function isExpectedInviteRedeemError(err: unknown): boolean {
  return err instanceof ApiError && EXPECTED_REDEEM_STATUSES.has(err.status);
}

export function buildInviteUrl(token: string): string {
  const url = new URL('/auth/invite', window.location.origin);
  url.searchParams.set('token', token);
  return url.toString();
}

interface RedeemInviteResponse {
  success: boolean;
  data: {
    customToken: string;
    email: string;
    requiresPassword: boolean;
  };
}

/** Call API to redeem an invite token and get a Firebase custom token */
export async function redeemInvite(token: string) {
  const res = await fetch(`${config.apiUrl}/invites/redeem`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => null) as { error?: { message?: string; code?: string } } | null;
    throw new ApiError(
      res.status,
      body?.error?.message ?? `Invite redemption failed: ${res.status}`,
      body?.error?.code,
    );
  }

  const json = (await res.json()) as RedeemInviteResponse;
  return json.data;
}
